import { ViewService, ViewServiceCategory } from '@xeetr/types/models'
import { Copy, Ellipsis, Trash2 } from 'lucide-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import CustomToggleButton from '../../inputs/CustomToggleButton'
import FloatingLabel from '../../inputs/FloatingLabel'
import LabeledInputNumber from '../../inputs/LabeledInputNumber'
import LabeledInputText from '../../inputs/LabeledInputText'

import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/ui/color-picker'
import { MultiSelect } from '@/components/ui/multi-select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useTenant } from '@/hooks/useTenant'
import { cn } from '@/lib/utils'

interface Props {
  onCopy: () => void
  onDelete: () => void
}

const ServiceContent = ({ onCopy, onDelete }: Props) => {
  const { control } = useFormContext<ViewService>()
  const name = useWatch({ control, name: 'name' })
  const { tenant } = useTenant()
  const categories: ViewServiceCategory[] = tenant?.categories || []

  return (
    <div className="relative">
      {/* Options Menu */}
      <div className="absolute right-0 top-0">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon">
              <Ellipsis className="h-5 w-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-56" align="end" side="bottom" sideOffset={5}>
            <div className="space-y-2">
              <p className="text-sm font-semibold">Optionen</p>
              <div className="flex items-center justify-between">
                <span className="text-sm">Aktiv</span>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Online Reservierbar</span>
                <Controller
                  name="onlineAvailable"
                  control={control}
                  render={({ field }) => (
                    <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
                  )}
                />
              </div>
              <Separator />
              <Button variant="ghost" className="w-full justify-start" onClick={() => onCopy()}>
                <Copy className="mr-2 h-4 w-4" />
                Duplizieren
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => onDelete()}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Content */}
      <div className="space-y-8">
        {/* Main Layout: Name & Description Left, Details Right */}
        <section>
          <h3 className="mb-4 text-lg font-semibold">Grundinformationen</h3>
          <div className="grid gap-6 md:grid-cols-[1fr_300px]">
            {/* Left Column: Name & Description */}
            <div className="space-y-4">
              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <LabeledInputText
                    label={'Name'}
                    id={field.name}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <FloatingLabel label="Beschreibung">
                    <Textarea rows={5} {...field} value={field.value || ''} />
                  </FloatingLabel>
                )}
              />
            </div>

            {/* Right Column: Category, Price & Duration */}
            <div className="space-y-4">
              <Controller
                name="categories"
                control={control}
                render={({ field, fieldState }) => (
                  <FloatingLabel label="Kategorien">
                    <MultiSelect
                      className={cn('w-full', {
                        'p-invalid': fieldState.error,
                      })}
                      value={field.value?.map((category) => category.id) || []}
                      onValueChange={(selectedIds) => {
                        const changed = categories.filter((category) =>
                          selectedIds.includes(category.id)
                        )
                        field.onChange(changed)
                      }}
                      options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
                      emptyState={
                        <div>
                          <p>Es wurden noch keine Kategorien erstellt.</p>
                          <p>
                            <Link to="/settings/categories" className="text-blue-600 underline">
                              Klicken Sie hier, um welche hinzuzufügen.
                            </Link>
                          </p>
                        </div>
                      }
                      placeholder="Wähle Kategorien aus."
                    />
                  </FloatingLabel>
                )}
              />
              <Controller
                control={control}
                name="price"
                render={({ field }) => (
                  <LabeledInputNumber
                    label="Preis"
                    inputId={field.name}
                    inputRef={field.ref}
                    value={field.value as number}
                    min={0}
                    suffix="€"
                    locale="de-DE"
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="duration"
                render={({ field }) => (
                  <LabeledInputNumber
                    label="Dauer (in Minuten)"
                    inputId={field.name}
                    inputRef={field.ref}
                    value={field.value as number}
                    suffix="min"
                    min={0}
                    onChange={field.onChange}
                  />
                )}
              />
              <Controller
                control={control}
                name="buffer"
                render={({ field }) => (
                  <LabeledInputNumber
                    label="Pufferzeit danach (in Minuten)"
                    inputId={field.name}
                    inputRef={field.ref}
                    value={field.value as number}
                    suffix="min"
                    min={0}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        </section>

        {/* Darstellung */}
        <section>
          <h3 className="mb-4 text-lg font-semibold">Darstellung</h3>
          <Controller
            control={control}
            name="backgroundColor"
            render={({ field }) => (
              <div className="flex justify-evenly">
                <FloatingLabel label="Farbe">
                  <div>
                    <ColorPicker value={field.value ?? '#27C226'} onChange={field.onChange} />
                  </div>
                </FloatingLabel>
                <FloatingLabel label="Vorschau">
                  <div className="flex gap-3">
                    <CustomToggleButton label={name || ''} color={field.value} checked={true} />
                    <CustomToggleButton label={name || ''} color={field.value} checked={false} />
                  </div>
                </FloatingLabel>
              </div>
            )}
          />
        </section>
      </div>
    </div>
  )
}

export default ServiceContent
